import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useI18n } from "../lib/i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
    Sparkles, Crown, ShieldCheck, ChevronRight, ChevronLeft, X,
    TrendingUp, Activity,
} from "lucide-react";

const STEPS_TOTAL = 4;

const TIER_GRADIENTS = {
    platinum: "from-slate-900 via-slate-800 to-indigo-900",
    gold: "from-amber-500 via-orange-500 to-rose-500",
    silver: "from-slate-400 via-slate-500 to-slate-600",
    bronze: "from-amber-700 via-orange-600 to-rose-600",
    starter: "from-indigo-500 via-purple-500 to-fuchsia-500",
};

const TIER_ICONS = {
    platinum: Crown, gold: Crown,
    silver: ShieldCheck, bronze: ShieldCheck,
    starter: Sparkles,
};

export default function WelcomeTour() {
    const { user, setUser } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        // Show tour only for consumers who haven't seen it
        if (!user) return;
        if (user.role !== "consumer") return;
        if (user.welcome_tour_seen_at) return;
        // Pre-fetch booster data
        api.get("/credit/boosters")
            .then((r) => {
                setData(r.data);
                setOpen(true);
            })
            .catch(() => {
                // If boosters endpoint fails, still show the tour minimally
                setOpen(true);
            });
    }, [user]);

    const close = async () => {
        setOpen(false);
        try {
            const r = await api.post("/auth/tour/seen");
            if (user && r.data) {
                const updated = { ...user, welcome_tour_seen_at: r.data.welcome_tour_seen_at };
                setUser(updated);
                try {
                    localStorage.setItem("opc_user", JSON.stringify(updated));
                } catch (_e) {
                    // localStorage may be unavailable in private mode
                }
            }
        } catch (_e) {
            // best-effort — never block close on persistence failure
        }
    };

    if (!user || user.role !== "consumer" || user.welcome_tour_seen_at) return null;

    const tierKey = data?.tier?.key || "starter";
    const tierLabel = data?.tier?.label || "Starter";
    const TierIcon = TIER_ICONS[tierKey] || Sparkles;
    const gradient = TIER_GRADIENTS[tierKey] || TIER_GRADIENTS.starter;
    const score = data?.current_score || user?.credit_score || 600;
    const grade = score >= 800 ? "Excellent" : score >= 740 ? "Very Good" : score >= 670 ? "Good" : score >= 580 ? "Fair" : "Poor";
    const firstName = user.full_name?.split(" ")[0] || "Friend";
    const unlocked = data?.unlocked_count || 0;
    const total = data?.total_count || 8;
    const lockedBoosters = (data?.boosters || []).filter((b) => !b.unlocked).sort((a, b) => b.points - a.points);
    const nextBest = lockedBoosters[0];

    const TIER_THRESHOLDS = { starter: 1, bronze: 3, silver: 5, gold: 7 };
    const needed = Math.max(1, (TIER_THRESHOLDS[tierKey] || 1) - unlocked);

    const next = () => (step < STEPS_TOTAL - 1 ? setStep(step + 1) : close());
    const back = () => step > 0 && setStep(step - 1);
    const takeMeThere = async () => {
        await close();
        if (nextBest?.action_url) nav(nextBest.action_url);
    };

    const showCta = step === STEPS_TOTAL - 1 && nextBest;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && close()}>
            <DialogContent
                className="max-w-sm rounded-3xl p-0 overflow-hidden border-0 bg-white"
                data-testid="welcome-tour-modal"
            >
                <VisuallyHidden>
                    <DialogTitle>Welcome tour</DialogTitle>
                    <DialogDescription>
                        A short walkthrough of your Credit DNA score, tier, perks, and next quick win.
                    </DialogDescription>
                </VisuallyHidden>
                {/* Header gradient */}
                <div className={`bg-gradient-to-br ${gradient} p-6 pb-8 text-white relative`}>
                    <button
                        onClick={close}
                        className="absolute top-4 right-4 size-8 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-white/25 transition-colors"
                        data-testid="tour-close"
                        aria-label="Close tour"
                    >
                        <X size={16} />
                    </button>

                    {/* Step indicator */}
                    <div className="flex gap-1.5 mb-5">
                        {Array.from({ length: STEPS_TOTAL }).map((_, i) => (
                            <div
                                key={i}
                                className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-white" : "bg-white/30"}`}
                            />
                        ))}
                    </div>

                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-bold">
                        {t("tour.step", { n: step + 1, total: STEPS_TOTAL })}
                    </div>

                    {/* Step header content */}
                    {step === 0 && (
                        <div className="mt-3" data-testid="tour-step-1">
                            <Sparkles size={36} className="mb-3" />
                            <h2 className="font-display text-2xl font-bold leading-tight">
                                {t("tour.s1.title", { name: firstName })}
                            </h2>
                        </div>
                    )}
                    {step === 1 && (
                        <div className="mt-3" data-testid="tour-step-2">
                            <Activity size={36} className="mb-3" />
                            <div className="flex items-baseline gap-2">
                                <h2 className="font-display text-5xl font-bold leading-none">{score}</h2>
                                <div className="text-sm text-white/80 font-semibold">{grade}</div>
                            </div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-bold mt-1">
                                Credit DNA Score
                            </div>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="mt-3" data-testid="tour-step-3">
                            <TierIcon size={36} className="mb-3" fill="currentColor" />
                            <h2 className="font-display text-2xl font-bold leading-tight">
                                {t("tour.s3.title", { tier: tierLabel })}
                            </h2>
                            <div className="mt-3 inline-flex items-center gap-1.5 bg-white/15 backdrop-blur rounded-full px-3 py-1 text-xs font-bold">
                                {unlocked} / {total} boosters
                            </div>
                        </div>
                    )}
                    {step === 3 && (
                        <div className="mt-3" data-testid="tour-step-4">
                            <TrendingUp size={36} className="mb-3" />
                            <h2 className="font-display text-2xl font-bold leading-tight">
                                {t("tour.s4.title")}
                            </h2>
                        </div>
                    )}
                </div>

                {/* Body content */}
                <div className="px-6 py-5 -mt-3 bg-white">
                    {step === 0 && (
                        <p className="text-sm text-slate-600 leading-relaxed">
                            {t("tour.s1.body")}
                        </p>
                    )}
                    {step === 1 && (
                        <p className="text-sm text-slate-600 leading-relaxed">
                            {t("tour.s2.body", { score, grade })}
                        </p>
                    )}
                    {step === 2 && (
                        <div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                {tierKey === "starter"
                                    ? t("tour.s3.body_starter", { needed })
                                    : t("tour.s3.body", { tier: tierLabel, count: unlocked, total })}
                            </p>
                            {data?.perks && (data.perks.loan_interest_discount > 0 || data.perks.mmf_yield_boost > 0 || data.perks.deposit_apr_boost > 0) && (
                                <div className="mt-4 grid grid-cols-3 gap-2">
                                    {data.perks.loan_interest_discount > 0 && (
                                        <PerkPill label={t("perks.loan_discount")} value={`−${(data.perks.loan_interest_discount * 100).toFixed(2).replace(/\.?0+$/, "")}%`} />
                                    )}
                                    {data.perks.mmf_yield_boost > 0 && (
                                        <PerkPill label={t("perks.mmf_boost")} value={`+${(data.perks.mmf_yield_boost * 100).toFixed(0)}%`} />
                                    )}
                                    {data.perks.deposit_apr_boost > 0 && (
                                        <PerkPill label={t("perks.deposit_boost")} value={`+${(data.perks.deposit_apr_boost * 100).toFixed(2).replace(/\.?0+$/, "")}%`} />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {step === 3 && (
                        <div>
                            {nextBest ? (
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    <span className="font-semibold text-[#0A1930]">{nextBest.title}</span> (+{nextBest.points} pts). {nextBest.description}
                                </p>
                            ) : (
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    {t("tour.s4.body_max")}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Footer actions */}
                    <div className="mt-6 flex items-center justify-between gap-3">
                        {step > 0 ? (
                            <Button
                                onClick={back}
                                variant="ghost"
                                size="sm"
                                className="h-10 text-slate-500 hover:text-slate-900"
                                data-testid="tour-back"
                            >
                                <ChevronLeft size={14} /> {t("tour.back")}
                            </Button>
                        ) : (
                            <button
                                onClick={close}
                                className="text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors"
                                data-testid="tour-skip"
                            >
                                {t("tour.skip")}
                            </button>
                        )}

                        {showCta ? (
                            <div className="flex gap-2">
                                <Button
                                    onClick={close}
                                    variant="outline"
                                    size="sm"
                                    className="h-10 rounded-xl font-semibold"
                                    data-testid="tour-got-it"
                                >
                                    {t("tour.done")}
                                </Button>
                                <Button
                                    onClick={takeMeThere}
                                    size="sm"
                                    className="h-10 rounded-xl font-semibold bg-[#0A1930] hover:bg-[#0A1930]/90 text-white"
                                    data-testid="tour-take-me-there"
                                >
                                    {t("tour.s4.cta")} <ChevronRight size={14} />
                                </Button>
                            </div>
                        ) : (
                            <Button
                                onClick={next}
                                size="sm"
                                className="h-10 rounded-xl font-semibold bg-[#0A1930] hover:bg-[#0A1930]/90 text-white"
                                data-testid="tour-next"
                            >
                                {step === STEPS_TOTAL - 1 ? t("tour.done") : t("tour.next")}
                                <ChevronRight size={14} />
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PerkPill({ label, value }) {
    return (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5">
            <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-600 leading-tight">
                {label}
            </div>
            <div className="font-display text-base font-bold text-emerald-700 mt-1">{value}</div>
        </div>
    );
}
